// src/api/api.js
import { print } from "graphql";
import { GraphQLClient } from "graphql-request";
import { checkStaleSession } from "../util";
import config from "../config.json";

import MenuQuery from "./queries/MenuQuery.gql";
import ProductQuery from "./queries/ProductQuery.gql";
import RetailerQuery from "./queries/RetailerQuery.gql";
import RetailersQuery from "./queries/RetailersQuery.gql";
import UpdateCheckout from "./queries/UpdateCheckout.gql";
import CreateCheckout from "./queries/CreateCheckout.gql";
import GetSpecialsList from "./queries/GetSpecialsList.gql";
import MenuByStaffPicks from "./queries/MenuByStaffPicks.gql";
import FetchCartDetails from "./queries/FetchCartDetails.gql";
import AddItemToCheckout from "./queries/AddItemToCheckout.gql";
import UpdateItemQuantity from "./queries/UpdateItemQuantity.gql";
import OrdersByOrderNumber from "./queries/OrdersByOrderNumber.gql";
import MenuByCustomSection from "./queries/MenuByCustomSection.gql";
import MenuBySpecialsSection from "./queries/MenuBySpecialsSection.gql";
import RemoveItemFromCheckout from "./queries/RemoveItemFromCheckout.gql";

const { apiEndpoint } = rangeHeadlessApi;

// Multi-key functionality removed
function getMultiKeyRequestIndex(variables) {
    return null; // Multi-key functionality removed
}

function processCategoryFilter(variables) {
    if (variables && variables.menuFilter && Array.isArray(variables.menuFilter.category)) {
        if (variables.menuFilter.category.length === 1) {
            variables.menuFilter.category = variables.menuFilter.category[0];
        } else if (variables.menuFilter.category.length > 1) {
            variables.menuFilter.categories = variables.menuFilter.category;
            delete variables.menuFilter.category;
        } else {
            delete variables.menuFilter.category;
        }
    }
    return variables;
}

// Set up rate limiting
const RATE_LIMIT = {
    maxRequests: 20,       // Maximum requests per time window
    timeWindow: 1000,     // Time window in milliseconds (1 second)
    queue: [],           // Queue for pending requests
    requestCount: 0,      // Current request count in the time window
    windowStart: Date.now(), // Start of the current time window
    processingQueue: false, // Flag to prevent multiple queue processing
    lastRateLimitHit: null  // Track the last time we hit a rate limit
};

// Process the request queue
async function processQueue() {
    if (RATE_LIMIT.processingQueue || RATE_LIMIT.queue.length === 0) return;

    RATE_LIMIT.processingQueue = true;

    try {
        while (RATE_LIMIT.queue.length > 0) {
            const now = Date.now();

            // If we've hit a rate limit recently, add additional delay
            if (RATE_LIMIT.lastRateLimitHit && now - RATE_LIMIT.lastRateLimitHit < 15000) {
                const cooldownTime = 15000 - (now - RATE_LIMIT.lastRateLimitHit);
                await new Promise(resolve => setTimeout(resolve, cooldownTime));
            }

            // Reset counter if we're in a new time window
            if (now - RATE_LIMIT.windowStart > RATE_LIMIT.timeWindow) {
                RATE_LIMIT.requestCount = 0;
                RATE_LIMIT.windowStart = now;
            }

            // Wait if we've hit the rate limit
            if (RATE_LIMIT.requestCount >= RATE_LIMIT.maxRequests) {
                const waitTime = RATE_LIMIT.windowStart + RATE_LIMIT.timeWindow - now + 50;
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
            }

            // Process the next request in the queue
            const { execute, resolve, reject } = RATE_LIMIT.queue.shift();
            RATE_LIMIT.requestCount++;

            try {
                const result = await execute();
                resolve(result);
            } catch (error) {
                // Handle 429 errors specially
                if (error.response && error.response.status === 429) {
                    console.warn('Rate limit hit, delaying subsequent requests');
                    RATE_LIMIT.lastRateLimitHit = Date.now();
                    // Force a longer delay for the next requests
                    RATE_LIMIT.requestCount = RATE_LIMIT.maxRequests;
                }
                reject(error);
            }
        }
    } finally {
        RATE_LIMIT.processingQueue = false;
    }
}

// Rate-limited API client configuration
const dutchieQueryClient = new GraphQLClient(apiEndpoint, {
    errorPolicy: "all",
    timeout: 10000, // 10 second timeout
});

const dutchieMutationClient = new GraphQLClient(apiEndpoint, {
    timeout: 10000, // 10 second timeout
});

// Cache for identical requests within a short time window
const requestCache = new Map();
const CACHE_TTL = 60000; // 1 minute cache TTL

// Rate-limited query function
export async function dutchieQuery(queryName, variables) {
    if (checkStaleSession && checkStaleSession()) { // Ensure function exists before calling
        console.warn("[dutchieQuery] Stale session detected, aborting query.");
        return Promise.reject(new Error("Stale session")); // Or handle as appropriate
    }

    // Deep clone variables at the beginning to avoid unintended modifications
    let mutableVariables = JSON.parse(JSON.stringify(variables || {}));

    // Default limit for MenuQuery (should be overridden by useMenuProducts for main lists)
    if (queryName === "MenuQuery" && typeof mutableVariables.limit === 'undefined') {
        console.warn(`[dutchieQuery] ${queryName} called without explicit limit, applying default of 20. This might be unexpected if useMenuProducts should set it.`);
        mutableVariables.limit = 20;
    }
    
    // Process category filters on the cloned variables
    const processedVariables = processCategoryFilter(mutableVariables);
    const gqlQueryDocument = getQuery(queryName);

    if (!gqlQueryDocument) {
        console.error(`[dutchieQuery] Unknown queryName: ${queryName}`);
        return Promise.reject(new Error(`Unknown queryName: ${queryName}`));
    }
    const gqlQueryString = print(gqlQueryDocument);

    // --- Primary Logging Location ---
    console.log(
        `[Dutchie API Request] Name: ${queryName}`,
        `\nVariables: ${JSON.stringify(processedVariables, null, 2)}`, // Pretty print variables
        // `\nQuery:\n${gqlQueryString}` // Uncomment to log the full query string if needed, can be long
    );

    // Generate a cache key for this request
    const cacheKey = `${queryName}:${JSON.stringify(variables)}`;

    // Check cache first
    const cachedItem = requestCache.get(cacheKey);
    if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL) {
        return cachedItem.data;
    }
    
    if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL) {
        console.log(`[dutchieQuery] Cache HIT for key: ${cacheKey}`);
        return cachedItem.data;
    } else if (cachedItem) {
        console.log(`[dutchieQuery] Cache STALE for key: ${cacheKey}`);
    } else {
        console.log(`[dutchieQuery] Cache MISS for key: ${cacheKey}`);
    }

    // Return a promise that will be resolved when the request is processed
    return new Promise((resolve, reject) => {
        RATE_LIMIT.queue.push({
            execute: async () => {
                try {
                    let requestHeaders = null;
                    if (queryName === "OrdersByOrderNumber") {
                        requestHeaders = { "x-rh-order": "true" };
                    }

                    const keyIndexHeader = getMultiKeyRequestIndex(variables);
                    if (keyIndexHeader !== null) {
                        requestHeaders = requestHeaders || {};
                        requestHeaders["x-rh-keyindex"] = keyIndexHeader.toString();
                    }

                    // Process category filters
                    const processedVariables = processCategoryFilter(variables);

                    // Execute the query with retry logic
                    const result = await executeWithRetry(() =>
                        dutchieQueryClient.rawRequest(
                            print(getQuery(queryName)),
                            processedVariables,
                            requestHeaders
                        )
                    );

                    // Cache the result
                    requestCache.set(cacheKey, {
                        data: result,
                        timestamp: Date.now()
                    });

                    return result;
                } catch (error) {
                    console.error(`Error executing ${queryName}:`, error);
                    throw error;
                }
            },
            resolve,
            reject
        });

        // Start processing the queue
        processQueue();
    });
}

// Similar changes for dutchieMutation
export async function dutchieMutation(queryName, variables) {
    if (checkStaleSession()) return;

    return new Promise((resolve, reject) => {
        RATE_LIMIT.queue.push({
            execute: async () => {
                let requestHeaders = null;
                const keyIndexHeader = getMultiKeyRequestIndex(variables);

                if (keyIndexHeader !== null) {
                    requestHeaders = {
                        "x-rh-keyindex": keyIndexHeader.toString()
                    };
                }

                return await executeWithRetry(() =>
                    dutchieMutationClient.request(
                        print(getQuery(queryName)),
                        variables,
                        requestHeaders
                    )
                );
            },
            resolve,
            reject
        });

        processQueue();
    });
}

// Retry logic with exponential backoff
async function executeWithRetry(fn, maxRetries = 2) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // Don't retry on 429 errors
            if (error.response && error.response.status === 429) {
                console.warn('Rate limit hit, not retrying');
                throw error;
            }

            // Don't retry on the last attempt
            if (attempt === maxRetries) {
                throw error;
            }

            // Exponential backoff delay
            const delay = Math.min(1000 * (2 ** attempt), 10000);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    throw lastError;
}

/**
 * Get GQL query from string.
 * @param {string} queryName
 * @returns
 */
const getQuery = (queryName) => {
    switch (queryName) {
        case "RetailerQuery":
            return RetailerQuery;
        case "RetailersQuery":
            return RetailersQuery;
        case "FetchCartDetails":
            return FetchCartDetails;
        case "CreateCheckout":
            return CreateCheckout;
        case "GetSpecialsList":
            return GetSpecialsList;
        case "RemoveItemFromCheckout":
            return RemoveItemFromCheckout;
        case "UpdateItemQuantity":
            return UpdateItemQuantity;
        case "AddItemToCheckout":
            return AddItemToCheckout;
        case "MenuByStaffPicks":
            return MenuByStaffPicks;
        case "MenuBySpecialsSection":
            return MenuBySpecialsSection;
        case "MenuQuery":
            return MenuQuery;
        case "ProductQuery":
            return ProductQuery;
        case "UpdateCheckout":
            return UpdateCheckout;
        case "OrdersByOrderNumber":
            return OrdersByOrderNumber;
        case "MenuByCustomSection":
            return MenuByCustomSection;
        default:
            return false;
    }
};

/**
 * Fuzzy search to get address suggestions.
 * @param {string} query URI encoded string to query.
 * @returns {Promise|error}
 */
export const getAddressSuggestions = async (query) => {
    const requestOptions = {
        method: "GET",
        redirect: "follow",
    };

    try {
        const response = await fetch(
            `https://api.tomtom.com/search/2/search/${query}.json?key=${config.TOMTOMAPI}&countrySet=US,CA&idxSet=PAD,Addr`,
            requestOptions
        );
        return await response.json();
    } catch (error) {
        return console.log("error", error);
    }
};

// Periodically clean up the request cache to prevent memory leaks
setInterval(() => {
    const now = Date.now();
    for (const [key, value] of requestCache.entries()) {
        if (now - value.timestamp > CACHE_TTL) {
            requestCache.delete(key);
        }
    }
}, 60000); // Run cleanup every minute
